// src/pages/SavedJobs.tsx

import React from "react";
import { supabase } from "../lib/supabaseClient";

type SavedJob = {
  id?: string | number;
  title?: string | null;
  company?: string | null;
  location?: string | null;
  url: string;
  jd_text?: string | null;
  saved_at?: string | number | null;
};

export default function SavedJobs() {
  const API =
    process.env.REACT_APP_API ?? "http://localhost:8000";

  const [jobs, setJobs] = React.useState<SavedJob[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [removingUrl, setRemovingUrl] =
    React.useState<string | null>(null);

  const [appliedUrls, setAppliedUrls] =
    React.useState<Set<string>>(new Set());

  const getAccessToken = React.useCallback(async () => {
    const { data } = await supabase.auth.getSession();

    return data.session?.access_token ?? null;
  }, []);

  const loadSavedJobs = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const token = await getAccessToken();

      if (!token) {
        setJobs([]);
        setError("Please sign in to view your saved jobs.");
        return;
      }

      console.log(
        "[SavedJobs] GET:",
        `${API}/api/saved-jobs`
      );

      const response = await fetch(
        `${API}/api/saved-jobs`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const raw = await response.text();

      if (!response.ok) {
        throw new Error(
          `Could not load saved jobs (${response.status}): ${raw}`
        );
      }

      const data = JSON.parse(raw);

      setJobs(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("[SavedJobs] load error:", err);

      setError(
        err instanceof Error
          ? err.message
          : "Could not load saved jobs."
      );

      setJobs([]);
    } finally {
      setLoading(false);
    }
  }, [API, getAccessToken]);

  const loadAppliedJobs = React.useCallback(async () => {
    try {
      const token = await getAccessToken();

      if (!token) {
        setAppliedUrls(new Set());
        return;
      }

      const response = await fetch(
        `${API}/api/applications`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        return;
      }

      const data = await response.json();

      const urls = Array.isArray(data)
        ? data
            .map((item: any) => item.url)
            .filter(
              (url: any): url is string =>
                typeof url === "string"
            )
        : [];

      setAppliedUrls(new Set(urls));
    } catch (err) {
      console.error(
        "[SavedJobs] load applications error:",
        err
      );
    }
  }, [API, getAccessToken]);

  React.useEffect(() => {
    void loadSavedJobs();
    void loadAppliedJobs();
  }, [loadSavedJobs, loadAppliedJobs]);

  const removeSavedJob = async (url: string) => {
    try {
      setRemovingUrl(url);
      setError("");

      const token = await getAccessToken();

      if (!token) {
        setError("Please sign in first.");
        return;
      }

      const response = await fetch(
        `${API}/api/saved-jobs`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            url,
          }),
        }
      );

      const raw = await response.text();

      if (!response.ok) {
        throw new Error(
          `Could not remove saved job (${response.status}): ${raw}`
        );
      }

      setJobs((current) =>
        current.filter((job) => job.url !== url)
      );
    } catch (err) {
      console.error("[SavedJobs] remove error:", err);

      setError(
        err instanceof Error
          ? err.message
          : "Could not remove the saved job."
      );
    } finally {
      setRemovingUrl(null);
    }
  };

  const markAsApplied = async (job: SavedJob) => {
    try {
      setError("");

      const token = await getAccessToken();

      if (!token) {
        setError("Please sign in first.");
        return;
      }

      const response = await fetch(
        `${API}/api/applications`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            url: job.url,
            title: job.title ?? "",
            company: job.company ?? "",
            location: job.location ?? "",
            status: "applied",
            notes: "",
          }),
        }
      );

      const raw = await response.text();

      if (!response.ok) {
        throw new Error(
          `Could not add application (${response.status}): ${raw}`
        );
      }

      setAppliedUrls((current) => {
        const next = new Set(current);
        next.add(job.url);
        return next;
      });

      window.dispatchEvent(
        new Event("applications:changed")
      );
    } catch (err) {
      console.error("[SavedJobs] mark as applied error:", err);

      setError(
        err instanceof Error
          ? err.message
          : "Could not add this job to Applications."
      );
    }
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-border bg-white p-6">
        <p className="text-sm text-gray-500">
          Loading saved jobs...
        </p>
      </div>
    );
  }

  if (!jobs.length) {
    return (
      <div className="rounded-2xl border border-border bg-white p-6">
        <h2 className="text-lg font-semibold text-gray-900">
          Saved Jobs
        </h2>

        <p className="mt-2 text-sm text-gray-500">
          No saved jobs yet. Save a job from your search results
          and it will appear here.
        </p>

        {error && (
          <p className="mt-3 text-sm text-red-600">
            {error}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-border bg-white p-4 md:p-6">
      <div className="mb-4 flex items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            Saved Jobs
          </h2>

          <p className="mt-1 text-sm text-gray-500">
            {jobs.length} saved{" "}
            {jobs.length === 1 ? "job" : "jobs"}
          </p>
        </div>
      </div>

      {error && (
        <p className="mb-4 text-sm text-red-600">
          {error}
        </p>
      )}

      <div className="space-y-3">
        {jobs.map((job) => (
          <article
            key={job.id ?? job.url}
            className="rounded-xl border border-gray-200 bg-gray-50 p-4"
          >
            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div className="min-w-0 flex-1">
                <h3 className="font-semibold text-gray-900">
                  {job.title || "Untitled job"}
                </h3>

                <p className="mt-1 text-sm text-gray-500">
                  {[job.company, job.location]
                    .filter(Boolean)
                    .join(" • ")}
                </p>

                {job.jd_text && (
                  <p className="mt-3 line-clamp-3 text-sm leading-6 text-gray-600">
                    {job.jd_text}
                  </p>
                )}
              </div>

              <div className="flex shrink-0 items-center gap-2">
                <a
                  href={job.url}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-lg border border-violet-200 bg-white px-4 py-2 text-sm font-semibold text-violet-700 transition hover:bg-violet-50"
                >
                  View Job
                </a>

                <button
                  type="button"
                  disabled={appliedUrls.has(job.url)}
                  onClick={() => {
                    if (appliedUrls.has(job.url)) return;

                    void markAsApplied(job);
                  }}
                  className={
                    appliedUrls.has(job.url)
                      ? "rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700 cursor-default"
                      : "rounded-lg border border-emerald-200 bg-white px-4 py-2 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-50"
                  }
                >
                  {appliedUrls.has(job.url)
                    ? "✓ Applied"
                    : "Mark as Applied"}
                </button>

                <button
                  type="button"
                  disabled={removingUrl === job.url}
                  onClick={() =>
                    void removeSavedJob(job.url)
                  }
                  className="rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {removingUrl === job.url
                    ? "Removing..."
                    : "Remove"}
                </button>
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
} 